import { useEffect } from 'react';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import { Button, Skeleton } from '@radix-ui/themes';
import classNames from 'classnames';
import { Link, useParams } from 'react-router-dom';
import FITUrls from 'helpers/FITUrls';
import { getSurfaceType, openInExternalUrl } from 'helpers/utils';
import AdKaoraBanner, { AdSlot } from 'ui/design-base/AdKaoraBanner';
import CompetitionObject from 'ui/design-base/CompetitionObject';
import Container from 'ui/design-base/Container';
import DrawChampionshipObject from 'ui/design-base/DrawChampionshipObject';
import ErrorBox from 'ui/design-base/ErrorBox';
import NoMenuHeader from 'ui/design-base/NoMenuHeader';
import Page from 'ui/design-base/Page';
import PlayerUserObject from 'ui/design-base/PlayerUserObject';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchTournament } from 'store/tournament/tournamentSlice';
import type { DrawType } from 'types/draw';

export const TournamentPage = () => {
  const { id } = useParams<string>();
  const dispatch = useAppDispatch();
  const { tournament, loading, error } = useAppSelector((state) => state.tournament);

  useEffect(() => {
    dispatch(fetchTournament(id));
  }, [id, dispatch]);

  const getPageDescription = () => {
    const competition = tournament?.competition;
    return `Pagina del Torneo: ${tournament?.name} - della competizione ${competition?.name}  - Numero partecipanti: ${
      tournament?.players_count
    } - Lista tabelloni: ${tournament?.draws?.map((m: DrawType) => m.name)}`;
  };

  const getPageDescriptionChampionship = () => {
    const competition = tournament?.competition;
    return `Pagina della fase della competizione a squadre: ${tournament?.name} - di ${
      competition?.name
    }  - Numero squadre partecipanti: ${tournament?.players_count} - Lista incontri: ${tournament?.draws?.map(
      (m: DrawType) => m.name,
    )}`;
  };

  const getPageKeyword = () => {
    const competition = tournament?.competition;
    return `${tournament?.name} | competizione: ${competition?.name} | Numero partecipanti: ${
      tournament?.players_count
    } | ${tournament?.draws?.map((m: DrawType) => `${m.name} | `)}`;
  };

  const renderContent = () => {
    const drawsTable = (
      <Container title='Tabelloni' className='mt-3'>
        <div className='flex w-full flex-col flex-wrap gap-2 overflow-auto'>
          {tournament?.draws?.map((draw: DrawType) => (
            <div className='rounded-xl bg-white p-2' key={draw.id}>
              <Link className='w-full' to={`/tabellone/${draw.id}`}>
                <div
                  id='competition'
                  className='flex flex-row content-center items-center gap-2 rounded-xl p-1 hover:bg-neutral-neutral-1'
                >
                  <Skeleton loading={loading} className='h-auto w-auto'>
                    <div
                      id='subscribers'
                      className='flex items-center gap-2 rounded-xl border border-solid border-neutral-neutral-2 bg-white p-3 text-base font-medium text-neutral-neutral-12'
                    >
                      <div className='text-sm font-medium text-neutral-neutral-8'>Iscritti</div>
                      <div className='text-2xl font-bold text-neutral-neutral-alpha-12'>{draw.subcribers_count}</div>
                    </div>
                  </Skeleton>
                  <Skeleton loading={loading} className='h-auto w-auto'>
                    <div id='competitionName' className='text-xl font-medium tracking-tight text-neutral-neutral-12'>
                      {draw.name === '' ? 'Tabellone FITP' : draw.name}
                    </div>
                  </Skeleton>
                </div>
              </Link>
              <Skeleton loading={loading} className='h-auto w-auto'>
                <div id='external-link' className='flex justify-end p-1'>
                  <Button
                    variant='outline'
                    size='2'
                    className='min-w-28 cursor-pointer'
                    onClick={() => openInExternalUrl(FITUrls.draw(draw.fit_draw_id, tournament))}
                  >
                    Link FITP
                    <ArrowTopRightIcon />
                  </Button>
                </div>
              </Skeleton>
              <div className='mt-3 flex flex-col justify-center gap-3 md:flex-row'>
                <Skeleton loading={loading} className='h-auto w-auto'>
                  <div id='winner' className='font-bold'>
                    {draw.winner && (
                      <div className=''>
                        Vincitore:
                        <div className='rounded-md bg-accent-accent-3'>
                          <PlayerUserObject type={'fit_player_profile'} {...draw.winner} loading={loading} />
                        </div>
                      </div>
                    )}
                  </div>
                </Skeleton>
              </div>
            </div>
          ))}
        </div>
      </Container>
    );

    const championshipTable = (
      <Container title='Incontri' className='mt-3'>
        <div className='flex flex-col gap-2'>
          {tournament?.draws?.map((draw: DrawType) => (
            <DrawChampionshipObject loading={loading} {...draw} key={draw.id} />
          ))}
        </div>
      </Container>
    );

    const itemClassName = 'gap-2 p-3 rounded-xl border border-solid border-neutral-neutral-2';
    const labelClassName = 'self-stretch text-neutral-neutral-12 text-lg font-medium tracking-normal';
    const contentClassName =
      'flex justify-between self-stretch text-neutral-neutral-alpha-12 text-3xl md:text-4xl font-bold tracking-tighter leading-9 md:leading-10';

    return (
      <>
        <NoMenuHeader {...tournament} loading={loading} defaultName='Torneo FITP' />
        <div className='mt-3 flex flex-wrap gap-2'>
          <div id='info' className={classNames(itemClassName, 'w-full bg-neutral-neutral-1')}>
            <div id='title1' className={labelClassName}>
              Competizione
            </div>
            <Skeleton loading={loading} className='h-auto w-auto'>
              <div id='value1' className='py-2'>
                <CompetitionObject {...tournament?.competition} loading={loading} />
              </div>
            </Skeleton>
            <div id='columns' className='grid w-full grid-cols-2 flex-wrap items-start gap-2 self-stretch'>
              {tournament?.age_category && !tournament?.competition.championship && (
                <div id='category-field' className={classNames(itemClassName, 'bg-white')}>
                  <div id='category' className={labelClassName}>
                    Categoria di età
                  </div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament.age_category}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.players_count !== null && tournament?.players_count !== undefined && (
                <div id='subscribers' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>
                    {tournament?.competition.championship ? 'Squadre Iscritte' : 'Numero Iscritti'}
                  </div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament?.players_count}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.min_number_partecipants !== null && tournament?.min_number_partecipants !== undefined && (
                <div id='min_number_partecipants' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Minimo numero di partecipanti</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament?.min_number_partecipants}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.max_number_partecipants !== null && tournament?.max_number_partecipants !== undefined && (
                <div id='max_number_partecipants' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Massimo numero di partecipanti</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament?.max_number_partecipants}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.court_number !== null && tournament?.court_number !== undefined && (
                <div id='court_number' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Numero di campi</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament?.court_number}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.surface !== null && tournament?.surface !== undefined && (
                <div id='surface' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Superfice</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{getSurfaceType(tournament?.surface)}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.ball_type !== null && tournament?.ball_type !== undefined && (
                <div id='ball_type' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Tipologia di palle</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>{tournament?.ball_type}</div>
                  </Skeleton>
                </div>
              )}
              {tournament?.sign_up_order !== null && tournament?.sign_up_order !== undefined && (
                <div id='sign_up_order' className={classNames(itemClassName, 'bg-white')}>
                  <div className={labelClassName}>Criterio di selezione</div>
                  <Skeleton loading={loading} className='h-10 w-20'>
                    <div className={contentClassName}>
                      {tournament?.sign_up_order === 'ranking' ? 'Ordine di classifica' : 'Ordine di iscrizione'}
                    </div>
                  </Skeleton>
                </div>
              )}
            </div>
          </div>
        </div>
        {tournament?.competition?.championship ? championshipTable : drawsTable}
        {tournament?.list_participants?.length !== 0 && (
          <div className='mt-3 flex flex-wrap gap-2'>
            <div id='participants' className={classNames(itemClassName, 'w-full bg-neutral-neutral-1')}>
              <div id='title-participants' className={labelClassName}>
                Iscritti al torneo
              </div>
              <div id='table' className='w-full flex-wrap items-start gap-2 self-stretch'>
                <div
                  id='members'
                  className='mt-3 flex justify-between gap-2 self-stretch rounded-xl border border-solid border-neutral-neutral-2 bg-white p-3 text-base font-bold tracking-tighter text-neutral-neutral-alpha-12'
                >
                  <Skeleton loading={loading} className='h-auto w-auto'>
                    <div className={'grid w-full grid-cols-1 gap-2 xl:grid-cols-2'}>
                      {tournament?.list_participants?.map((participant) => (
                        <div
                          key={participant.id}
                          className='rounded-2xl border border-solid border-neutral-neutral-2 bg-neutral-neutral-1'
                        >
                          <PlayerUserObject
                            showRole={false}
                            key={participant.id}
                            {...participant}
                            linkToPlayer={true}
                            type={'fit_player_profile'}
                          />
                        </div>
                      ))}
                    </div>
                  </Skeleton>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const pageTitle = 'Torneo';
  let content = renderContent();
  let pageKeywords;
  let pageDescription = '';

  if (!loading) {
    if (!error && tournament) {
      pageKeywords = getPageKeyword();
      pageDescription = tournament?.competition?.championship ? getPageDescriptionChampionship() : getPageDescription();
    } else {
      content = (
        <div className='flex justify-center'>
          <ErrorBox
            errorType='Torneo non trovato'
            errorTitle='Qualcosa è andato storto nel caricamento di questo torneo'
            errorMessage='Prova più tardi. Se avrai nuovamente problemi, scrivici.'
          />
        </div>
      );
    }
  }

  return (
    <Page title={pageTitle} description={pageDescription || pageTitle} keywords={pageKeywords} showAds={true}>
      <AdKaoraBanner slot={AdSlot.ARTICLE_TOP} />
      {content}
      <AdKaoraBanner slot={AdSlot.ARTICLE_BOTTOM} />
    </Page>
  );
};

export default TournamentPage;
